import loadable from "@loadable/component";
import { Component, useState } from "react";
import { useTranslation } from "react-i18next";
import type { InfernoSiteAlertFragment } from "@ihr-radioedit/inferno-webapi";
import { removePrefix, trimText } from "../lib/utilities";
import { ILog } from "@ihr-radioedit/inferno-core";
import { localStorage } from "../lib/storage";
import { CloseButton, MagicLink } from "../ui";

const LocalAlert = loadable(() => import("./LocalAlert.component"));

const log = ILog.logger("LocalAlert.component");

export interface UserAlert extends InfernoSiteAlertFragment {
    count?: number;
    addClass?: string;
    override?: (key: string | number) => JSX.Element;
}

interface LocalAlertsProps {
    alert?: UserAlert | null;
    preventLoadAlerts?: boolean;
}

interface LocalAlertsState {
    alert?: UserAlert;
}

interface ReadMoreProps {
    content: string | null | undefined;
    showFullDesc: (show: boolean) => void;
}

const ReadMore = ({ content, showFullDesc }: ReadMoreProps) => {
    const { t } = useTranslation();
    if (content && content.length > LocalAlerts.AlertTextCutoff) {
        return (
            <span className="readmore" onClick={() => showFullDesc(true)}>
                {t("readmore")}
            </span>
        );
    }
    return null;
};

const getAlertTarget = (alert: UserAlert) => {
    if (alert.target) {
        return alert.target;
    } else if (
        typeof window !== "undefined" &&
        (!alert.link || alert.link[0] === "/" || alert.link.includes(window.location.host))
    ) {
        return "_self";
    } else {
        return "_blank";
    }
};

const AlertBody = ({ userAlert }: { userAlert: UserAlert }) => {
    const [fullTextVisible, showFullText] = useState(false);
    const alertText = userAlert.title
        ? fullTextVisible
            ? userAlert.title
            : trimText(userAlert.title, LocalAlerts.AlertTextCutoff, true)
        : null;
    return (
        <span className="message">
            <MagicLink
                to={userAlert.link || "#"}
                target={getAlertTarget(userAlert)}
                sectionName={removePrefix(/\ /g, "_")(alertText || "")}
                context="local_alert"
            >
                {alertText}
            </MagicLink>
            {fullTextVisible ? null : (
                <ReadMore content={userAlert.title} showFullDesc={(show: boolean) => showFullText(show)} />
            )}
        </span>
    );
};

export class LocalAlerts extends Component<LocalAlertsProps, LocalAlertsState> {
    private static readonly Threshold = 4;
    public static readonly AlertTextCutoff = 95;
    mounted = false;

    constructor(props: LocalAlertsProps) {
        super(props);

        const { alert, preventLoadAlerts } = props;
        let localAlert = {} as UserAlert;
        try {
            if (!preventLoadAlerts) {
                const lsAlert = localStorage.getItem("localAlert");
                if (lsAlert) {
                    localAlert = JSON.parse(lsAlert);
                }
            }
        } catch (err) {
            log.warn("Local alert data error", err);
        }

        if (alert && alert.title) {
            if (
                localAlert.title !== alert.title ||
                localAlert.start_date?.timestampMs !== alert.start_date?.timestampMs
            ) {
                localAlert = alert;
            }
        }
        localAlert.count = localAlert.count ? localAlert.count + 1 : 1;

        this.state = {
            alert: localAlert,
        };
    }

    componentDidMount() {
        this.saveAlerts();
        this.mounted = true;
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    componentDidUpdate() {
        this.saveAlerts();
    }

    saveAlerts() {
        if (!this.props.preventLoadAlerts) {
            localStorage.setItem("localAlert", JSON.stringify(this.state.alert));
        }
    }

    closeAlert() {
        const { alert } = this.state;
        if (alert && this.mounted) {
            alert.count = LocalAlerts.Threshold;
            this.setState({
                alert,
            });
        }
    }

    renderAlert = (alert: UserAlert) => {
        if (
            !alert.count ||
            alert.count >= LocalAlerts.Threshold ||
            (alert.start_date?.timestampMs || -Infinity) > Date.now() ||
            (alert.end_date?.timestampMs || Infinity) < Date.now() ||
            !alert.title
        ) {
            return null;
        }
        const localCloseAlert = () => this.closeAlert();

        return (
            <div className={`local-alert-content ${alert.addClass || ""}`}>
                <AlertBody userAlert={alert} />
                <CloseButton click={localCloseAlert} />
            </div>
        );
    };

    render() {
        const { alert } = this.state;
        if (!alert) {
            return null;
        }

        const renderedAlert = this.renderAlert(alert);

        if (!renderedAlert) {
            return null;
        }

        return <LocalAlert>{renderedAlert}</LocalAlert>;
    }
}

export default LocalAlerts;
